import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { login, setupAdmin } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSetup) {
        await setupAdmin(form.email, form.password, form.name);
        toast.success('Admin account created! Please login.');
        setIsSetup(false);
        setForm({ ...form, name: '' });
      } else {
        const res = await login(form.email, form.password);
        localStorage.setItem('lalita_admin_token', res.data.access_token);
        toast.success('Welcome back!');
        navigate('/admin/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Something went wrong';
      if (message.includes('Admin already exists')) {
        setIsSetup(false);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-sand flex items-center justify-center p-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-card p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-semibold text-charcoal">
              Lalita<span className="text-ubud-jungle ml-1">Admin</span>
            </h1>
            <p className="text-charcoal-muted text-sm mt-2">
              {isSetup ? 'Create your admin account' : 'Sign in to manage properties'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSetup && (
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Full Name</label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  required={isSetup}
                  data-testid="admin-name"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">Email</label>
              <Input
                type="email"
                placeholder="admin@lalita.id"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                required
                data-testid="admin-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="pr-10"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-muted hover:text-charcoal"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-lg py-3"
              data-testid="admin-submit-btn"
            >
              {loading ? 'Please wait...' : isSetup ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsSetup(!isSetup)}
              className="text-sm text-charcoal-muted hover:text-ubud-jungle"
            >
              {isSetup ? 'Already have an account? Sign in' : 'First time? Create admin account'}
            </button>
          </div>
        </div>

        <p className="text-center text-charcoal-muted text-xs mt-6">
          <Lock className="w-3 h-3 inline mr-1" />
          Secure admin access for Lalita Properties
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
